import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { CurrentProjectStateService } from '@docma-mm-core/project/current-project-state.service';
import JSZip from 'jszip';
import { environment } from 'src/environments/environment';
import { BIM_CONFIG } from '../../bp-common/config/config';
import { IOfflineService } from '../interfaces/offline-service.interface';
import { ConfigService } from '@docma-mm-core/config/config.service';
import { PlatformModeStateService } from './platform-mode-state.service';
import {LoggerEventService} from '@mydocma/common/src/lib/logger';
import { FileTransfer } from '@capacitor/file-transfer';
import { AuthService, JwtAuthService } from '@mydocma/auth';
import { Capacitor } from '@capacitor/core';
import { PersonalizationKeysEnum } from '@docma-mm-core/personalization/personalization-keys.enum';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class CapacitorOfflineService implements IOfflineService {
  private basePath = environment.appContainerConfig.capacitor.basePath.trim();
  private offlineDataPath = environment.appContainerConfig.capacitor.capacitorFolderPath.trim();
  private baseFolderPath = environment.appContainerConfig.capacitor.basePath.trim();
  private baseAssetsPath = environment.appContainerConfig.capacitor.assetsPath.trim();
  private materialIconsPath = environment.appContainerConfig.capacitor.assetsPath.trim() + '/material-icons';
  private bpApiBaseUrl: string;
  private mmApiBaseUrl: string;
  private urlFolderName: string;
  private createdFolders = new Set<string>([
    'live_update',
    'live_update/assets'
  ]);
  public currentProject;
  public userId;
  private jwtAuthService?:  JwtAuthService;

  constructor(private currentProjectState: CurrentProjectStateService,
              @Inject(BIM_CONFIG) private bimConfig: any,
              private configService: ConfigService,
              private platformModeStateService: PlatformModeStateService,
              private readonly logger: LoggerEventService,
              private injector:  Injector) {

    this.currentProjectState.getCurrentProject().subscribe((project) => {
      this.currentProject = project;
    });
    this.currentProjectState.getUserId().subscribe((userId) => {
      this.userId = userId;
    });

    this.getClientUrl();

    this.bpApiBaseUrl = bimConfig.bpApiUrl;
    this.mmApiBaseUrl = this.configService.getMmConfig().apiUrl;
  }
  private getAuthService(): JwtAuthService {
    if (!this.jwtAuthService) {
      this.jwtAuthService = this.injector.get(AuthService) as any as JwtAuthService;
    }
    return this.jwtAuthService;
  }

  async createUrlFolder(): Promise<void> {
    const urFolderPath = `${this.offlineDataPath}/${this.urlFolderName}`;
    await this.ensureFolderPath(urFolderPath);
    this.createdFolders.add(this.offlineDataPath);
    this.createdFolders.add(urFolderPath);
  }

  async getClientUrl(): Promise<void> {
    const clientUrl = await Preferences.get({key: PersonalizationKeysEnum.CapacitorClientUrl});
    if(clientUrl && clientUrl.value) {
      const urlFolderName = new URL(clientUrl.value).hostname;
      this.urlFolderName = urlFolderName;
      this.createUrlFolder();
    }
  }

  async getData(request: HttpRequest<unknown>): Promise<any> {
    const storagePath = await this.getStoragePathFromUrl(request.url);

    try {
      const result = await Filesystem.readFile({
        path: storagePath,
        directory: Directory.Data,
        encoding: Encoding.UTF8
      });

      if (request.url.includes('material-icons')){
        return result.data;
      }

      let data = result.data;
      if (data instanceof Blob) {
        data = await data.text();
      }

      let jsonData = JSON.parse(data);
      if (typeof jsonData === 'string'){
        jsonData = JSON.parse(jsonData);
      }

      const base64String = jsonData?.image;
      if (base64String){
        return this.base64ToBlob(base64String);
      }

      return jsonData;
    } catch (e) {
      console.log(`Loading file ${storagePath} failed. Using normal http request. Error = `, e);
      return null;
    }
  }

  async getAttachment(attachmentUid: string, fileName: string): Promise<Blob> {
    if (!this.currentProject?.customerId || !this.currentProject?.id) {
      throw new Error('Missing customerId or projectId from currentProject');
    }

    const basePath = this.getFolderPath("attachments") + "/" + attachmentUid;
    const filePath = `${basePath}/${fileName}`;

    let exists = true;
    try {
      await Filesystem.stat({
        path: filePath,
        directory: Directory.Data,
      });
    } catch {
      exists = false;
    }

    if (exists) {
      try {
        const file = await Filesystem.getUri({
          path: filePath,
          directory: Directory.Data,
        });

        const fileUrl = Capacitor.convertFileSrc(file.uri);
        const response = await fetch(fileUrl);

        if (!response.ok) {
          throw new Error(`Couldn't load attachment! status: ${response.status}`);
        }

        return await response.blob();
      } catch (error) {
        console.error(`Failed to read attachment at ${filePath}`, error);
        throw new Error(`Failed to read attachment at ${filePath}: ${error}`);
      }
    }

    //Fallback for older versions where attachments were saved with data.bin name
    const fallbackPath = `${basePath}/data.bin`;
    try {
      try {
        await Filesystem.stat({
          path: fallbackPath,
          directory: Directory.Data,
        });
      } catch (error) {
        throw new Error(`FILE_NOT_FOUND: Attachment not found at ${filePath} or fallback path ${fallbackPath}`);
      }

      const result = await Filesystem.readFile({
        path: fallbackPath,
        directory: Directory.Data,
        encoding: Encoding.UTF8
      });

      let data = result.data;
      if (data instanceof Blob) {
        data = await data.text();
      }

      // Handle both data URL and raw base64
      if (!data.includes('base64,')) {
        data = `data:application/octet-stream;base64,${data}`;
      }

      return this.base64ToBlob(data);
    } catch (error) {
      this.logger.notfifyError(error);
      throw new Error(`Failed to read fallback attachment at ${fallbackPath}: ${error}`);
    }
  }

  async saveDataByFolderPath(data: any, folderName: string, uid: string, childFolderName?: string): Promise<any>{
    if (!this.currentProject?.customerId || !this.currentProject?.id) {
      throw new Error('Missing customerId or projectId from currentProject');
    }
    const baseFolderPath = this.getFolderPath(folderName);
    let folderPath = `${baseFolderPath}/${uid}`;

    if (childFolderName){
      folderPath += '/' + childFolderName;
    }

    const filePath = `${folderPath}/data.json`;

    try {
      try {
        await Filesystem.mkdir({
          path: folderPath,
          directory: Directory.Data,
          recursive: true,
        });
      } catch (error) {
      }

      if (data instanceof Blob) {
        const base64String = await this.blobToBase64(data);
        data = base64String;
      }
      else{
        data = JSON.stringify(data);
      }

      await Filesystem.writeFile({
        path: filePath,
        data,
        directory: Directory.Data,
        encoding: Encoding.UTF8
      });

      console.log(filePath + ' saved successfully!');
    } catch (error) {
      this.logger.notfifyError(error);
    }

    return filePath;
  }

  async saveDataByUrl(request: HttpRequest<unknown>, data: any): Promise<any>{
    let filePath = request.url;

    if (filePath.includes('faults/search')){
      filePath += '/' + (request.body as any).UniqueIds[0];
    }
    const storagePath = this.getStoragePathFromUrl(filePath);
    const folderName = storagePath.replace(/\/[^\/]+\.[^\/]+$/, '');

    const responseType = request.responseType;
    try {
      try {
        await Filesystem.mkdir({
          path: folderName,
          directory: Directory.Data,
          recursive: true,
        });
      } catch (error) {
      }


      if (responseType === 'json' || responseType === 'text'){
        const result = await Filesystem.writeFile({
          path: storagePath,
          data: typeof data === 'string' ? data : JSON.stringify(data),
          directory: Directory.Data,
          encoding: Encoding.UTF8
        });
      } else if (responseType === 'blob'){
        const base64String = await this.blobToBase64(data);
        const jsonData = JSON.stringify(JSON.stringify({ image: base64String }));

        const result = await Filesystem.writeFile({
          path: storagePath,
          data: jsonData,
          directory: Directory.Data,
          encoding: Encoding.UTF8
        });
      }

      console.log(storagePath + ' saved successfully!');
    } catch (error) {
      this.logger.notfifyError(error);
    }
  }

  async checkNeedsUpdate(id: string, relativePath: string, date: Date): Promise<boolean> {
    const folderPath = this.getFolderPath('plans');
    const filePath = `${folderPath}/${id}/${relativePath}`;

    try {
      const isDownloaded = await this.isPlanDownloaded(id);
      if (isDownloaded){
        return false;
      }

      const lastModified = await this.getFileDate(filePath);
      return lastModified < date;
    } catch (error) {
      return true;
    }
  }

  async saveAttachment(data: File, attachmentUid: string): Promise<string> {
    if (!data || !attachmentUid) {
      throw new Error('Invalid input parameters');
    }

    const attachmentFolderPath = this.getFolderPath('attachments') + '/' + attachmentUid;
    const filePath = `${attachmentFolderPath}/data.bin`;

    try {
      try {
        await Filesystem.mkdir({
          path: attachmentFolderPath,
          directory: Directory.Data,
          recursive: true,
        });
      } catch (error) {

      }

      // Convert to base64 with data URL prefix
      const base64String = await this.blobToBase64(data);
      const fullBase64 = `data:${data.type};base64,${base64String}`;

      await Filesystem.writeFile({
        path: filePath,
        data: fullBase64,
        directory: Directory.Data,
        encoding: Encoding.UTF8
      });

      return filePath;
    } catch (error) {
      this.logger.notfifyError(error);
      throw new Error(`Failed to save attachment: ${error.message}`);
    }
  }

  async downloadAttachment(
    attachmentId: number,
    attachmentUid: string,
    fileName: string,
    extendedContentDisposition: boolean = false,
    width: number = 0,
    height: number = 0,
    originalWithScribbles: boolean = false
  ): Promise<string> {
    const attachmentFolderPath = this.getFolderPath("attachments") + "/" + attachmentUid;
    const filePath = `${attachmentFolderPath}/${fileName}`;
    try {
      await Filesystem.mkdir({
          path: attachmentFolderPath,
          directory: Directory.Data,
          recursive: true,
      });
    } catch (error) {

    }

    const endpoint = `${this.mmApiBaseUrl}/attachments/${attachmentId}`;
    const url = this.buildUrl(endpoint, {
      height,
      width,
      extendedContentDisposition,
      originalWithScribbles
    });

    const fileInfo = await Filesystem.getUri({
      directory: Directory.Data,
      path: filePath
    });

    try {
      await FileTransfer.downloadFile({
        url: url,
        path: fileInfo.uri,
        headers: this.getHeaders(),
        progress: true
      });

      return filePath;

    } catch (error) {
      console.error(`Attachment download failed: ${error}`);
      throw error;
    }
  }

  async isAttachmentDownloaded(attachmentUid: string): Promise<boolean> {
    const attachmentFolderPath = this.getFolderPath('attachments') + '/' + attachmentUid;
    try {
      const result = await Filesystem.readdir({
        path: attachmentFolderPath,
        directory: Directory.Data
      });
      return result.files.length > 0;
    } catch (error) {
      return false;
    }
  }

  private buildUrl(base: string, params: Record<string, any>): string {
    const url = new URL(base);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });
    return url.toString();
  }

  getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': 'Bearer ' + this.getAuthService()?.getAccessToken()
    };
  }

  async removeAttachment(attachmentUid: string): Promise<void> {
    if (!this.currentProject?.customerId || !this.currentProject?.id) {
      throw new Error('Missing customerId or projectId from currentProject');
    }

    const attachmentFolderPath = this.getFolderPath('attachments') + '/' + attachmentUid;

    try{
      await Filesystem.rmdir({
        path: attachmentFolderPath,
        directory: Directory.Data,
        recursive: true
      });

      console.log("Attachment removed successfully:", attachmentFolderPath);

    }catch (error) {
      if(!error.message?.includes('does not exist')){
        this.logger.notfifyError(error);
      }
    }
  }

  async removePlan(planId: string): Promise<boolean> {
    if (!this.currentProject?.customerId || !this.currentProject?.id) {
      throw new Error('Missing customerId or projectId from currentProject');
    }

    const planFolderPath = this.getFolderPath('plans') + '/' + planId;

    try {
      const result = await Filesystem.readdir({
        path: planFolderPath,
        directory: Directory.Data
      });

      for (const entry of result.files) {
        if (entry.type === 'directory' && ['1', '2', '3'].includes(entry.name)) {
          continue;
        }
        const entryPath = `${planFolderPath}/${entry.name}`;
        if (entry.type === 'directory') {
          await Filesystem.rmdir({
            path: entryPath,
            directory: Directory.Data,
            recursive: true
          });
        } else {
          await Filesystem.deleteFile({
            path: entryPath,
            directory: Directory.Data
          });
        }
      }

      return true;
    } catch (error) {
      this.logger.notfifyError(error);
      return false;
    }
  }

  async getFileDate(filePath: string): Promise<Date | null> {
    try {
      const fileInfo = await Filesystem.stat({
        path: filePath,
        directory: Directory.Data
      });

      return new Date(fileInfo.mtime);
    } catch (error) {
      return null;
    }
  }

  async unzipAndSave(zipBlob: Blob, planId: string, onlySaveBaseTiles: boolean): Promise<boolean> {
    try {
      if (!this.currentProject?.customerId || !this.currentProject?.id) {
        throw new Error('Missing customerId or projectId from currentProject');
      }

      const basePath = this.getFolderPath(`plans/${planId}`);

      try {
        await Filesystem.rmdir({
          path: basePath,
          directory: Directory.Data,
          recursive: true
        });
      } catch (error) {

      }

      const zip = await JSZip.loadAsync(zipBlob);
      await this.createAllNeededFolders(zip, basePath, onlySaveBaseTiles);

      const results = await Promise.allSettled(
        Object.entries(zip.files)
          .filter(([_, file]) => !file.dir)
          .map(([fullPath, file]) => this.processZipFile(fullPath, file, basePath, onlySaveBaseTiles))
      );

      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {

        this.logger.notfifyError(new Error(`Failed to save ${failures.length} files`));
        return false;
      }

      if (!onlySaveBaseTiles) {
        await this.markPlanAsDownloaded(planId);
      }

      return true;
    } catch (error) {
      this.logger.notfifyError(error);
      return false;
    }
  }

  async markPlanAsDownloaded(planId: string): Promise<void> {
    if (!this.currentProject?.customerId || !this.currentProject?.id) {
      throw new Error('Missing customerId or projectId from currentProject');
    }

    const filePath = this.getFolderPath(`plans/${planId}/.downloaded`);

    try {
      await Filesystem.writeFile({
        path: filePath,
        data: new Date().toISOString(),
        directory: Directory.Data,
        encoding: Encoding.UTF8
      });
    } catch (error) {
      this.logger.notfifyError(error);
      throw error;
    }
  }

  async isPlanDownloaded(planId: string): Promise<boolean> {
    if (!this.currentProject?.customerId || !this.currentProject?.id) {
      throw new Error('Missing customerId or projectId from currentProject');
    }

    const filePath = this.getFolderPath(`plans/${planId}/.downloaded`);

    try {
      await Filesystem.stat({
        path: filePath,
        directory: Directory.Data
      });
      return true;
    } catch {
      return false;
    }
  }

  private async processZipFile(fullPath: string, file: JSZip.JSZipObject, basePath: string, onlySaveBaseTiles: boolean): Promise<void> {
    const parts = fullPath.split('/').filter(p => p.length > 0);
    if (!fullPath.endsWith('.jpg') && !fullPath.endsWith('.jpeg') && !fullPath.endsWith('.png')){
      return;
    }

    let fileName = parts[parts.length - 1];
    fileName = fileName.replace(/\.[^/.]+$/, '.jpg');
    const folderPath = parts[parts.length - 2];
    if (onlySaveBaseTiles && +folderPath > 3){
      return;
    }

    const content = await file.async('base64');
    const fullFolderPath = `${basePath}/${folderPath}`;
    const fullCapacitorPath = [fullFolderPath, fileName].join('/');

    await Filesystem.writeFile({
      path: fullCapacitorPath,
      data: content,
      directory: Directory.Data,
    });
  }

  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  }

  private base64ToBlob(base64: string): Blob {
    // Check if the string is a Data URL or raw Base64
    const base64Data = base64.includes('base64,') ? base64.split(',')[1] : base64;

    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Try to get MIME type from Data URL or default to application/octet-stream
      const mimeType = base64.includes('data:')
        ? base64.match(/^data:(.*?);base64/)?.[1] || 'application/octet-stream'
        : 'application/octet-stream';

      return new Blob([byteArray], { type: mimeType });
    } catch (error) {
      this.logger.notfifyError(error);
      throw new Error('Invalid base64 string');
    }
  }

  private async createAllNeededFolders(zip: JSZip, basePath: string, onlySaveBaseTiles: boolean): Promise<void> {
    const folderSet = new Set<string>();

    for (const [fullPath, file] of Object.entries(zip.files)) {
      if (!fullPath.endsWith('.jpg') && !fullPath.endsWith('.jpeg') && !fullPath.endsWith('.png')){
        continue;
      }
      const parts = fullPath.split('/');
      const folderPath = parts[parts.length - 2];
      if (onlySaveBaseTiles && +folderPath > 3){
        continue;
      }
      folderSet.add(`${basePath}/${folderPath}`);
    }

    for (const folderPath of folderSet) {
      await this.ensureFolderPath(folderPath);
    }
  }

  async ensureFolderPath(folderPath: string) {
    const segments = folderPath.split('/').filter(p => p.length > 0);
    let currentPath = '';

    for (const segment of segments) {
      currentPath += (currentPath ? '/' : '') + segment;

      try {
        if (this.createdFolders.has(currentPath)) {
          continue;
        }

        await Filesystem.mkdir({
          path: currentPath,
          directory: Directory.Data,
          recursive: false
        });
        console.log(`Created folder at ${currentPath}`);
      } catch (err: any) {
        if (err.message?.includes('Directory exists')) {
        } else {
          console.warn(`Couldn't create folder at ${currentPath}`, err);
        }
      }
    }
  }

  private getStoragePathFromUrl(requestUrl: string): string {

    requestUrl = requestUrl.replace(this.mmApiBaseUrl, '');

    if (requestUrl.includes('assets/i18n')) {
      if (requestUrl.startsWith('./')) {
        requestUrl = requestUrl.substring(2);
      } else if (requestUrl.startsWith('.')) {
        requestUrl = requestUrl.substring(1);
      }
      return `${this.baseFolderPath}/${requestUrl}`;
    }

    if (requestUrl.includes('material-icons')) {
      const idx = requestUrl.indexOf('material-icons');
      if (idx !== -1) {
        requestUrl = requestUrl.substring(idx);
      }
      return `${this.baseAssetsPath}/${requestUrl}`;
    }

    let folderName = requestUrl
      .replace(/^(https?:)?\/\/[^\/]+\//, '')
      .replace(/^\.\//, '');

    folderName = folderName.replace(/^\/+/, '');

    folderName += '/data.json';

    if(requestUrl.includes('.well-known/openid-configuration') || requestUrl.includes('users/info')){
      return `${this.offlineDataPath}/${this.urlFolderName}/${folderName}`;
    };

    const basePath = `${this.offlineDataPath}/${this.urlFolderName}`;

    if (!this.userId) {
      return `${basePath}/${folderName}`;
    }
    if (folderName.toLocaleLowerCase().startsWith('mydocma')){
      return `${basePath}/users/${this.userId}/${folderName}`;
    }
    if (this.currentProject?.customerId && this.currentProject?.id){
      return `${basePath}/users/${this.userId}/customers/${this.currentProject.customerId}/projects/${this.currentProject.id}/${folderName}`;
    }

    return `${basePath}/${folderName}`;
  }

  private getFolderPath(folderName: string): string {
    return `${this.offlineDataPath}/${this.urlFolderName}/users/${this.userId}/customers/${this.currentProject.customerId}/projects/${this.currentProject.id}/${folderName}`;
  }

  private getMimeType(fileName: string): string {
    if (!fileName || !fileName.includes('.')) {
      return 'application/octet-stream';
    }

    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    const mimeTypes: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      bmp: 'image/bmp',
      svg: 'image/svg+xml',
      heic: 'image/heic',
      heif: 'image/heif',

      pdf: 'application/pdf',
      txt: 'text/plain',
      csv: 'text/csv',
      html: 'text/html',
      htm: 'text/html',
      json: 'application/json',
      xml: 'application/xml',

      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

      zip: 'application/zip',
      rar: 'application/vnd.rar',
      '7z': 'application/x-7z-compressed',

      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      m4a: 'audio/mp4',

      mp4: 'video/mp4',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      webm: 'video/webm'
    };

    return mimeTypes[extension] || 'application/octet-stream';
  }
}
